import { Menu, Text, UnstyledButton } from '@mantine/core';

import { brand } from '../../branding';
import { languageLabel, languageVariants } from '../engine';
import { OVERLAY_DROPDOWN_Z_INDEX } from './headerLayout';

import type { MediaItem } from '../types';


// The language switch of a document - in the search results table, in its mobile card, and in the
// document overlay's header.
//
// Deliberately NOT a Mantine <Select>: under this app's @uag/doka-design theme a Select renders as
// a grey input field whose own `variant="unstyled"` and background props are overruled, which reads
// as a disabled text box rather than as something to click (see chat: "der graue Hintergrund
// verwirrt" / "kenntlich machen, dass das eine Auswahlbox ist"). Built like the header's own
// language switch instead: a button whose every visual property sits in an inline style - the only
// thing that reliably beats that stylesheet - plus a caret, which is what marks it as a choice.
//
// A document with a single language variant has nothing to pick, so it stays plain text.

interface DocumentLanguagePickerProps {
    /** All variants of one logical document (see engine.groupMediaByDocument). */
    variants: MediaItem[];
    /** Language code of the variant currently shown; `''`/null for a variant without a language. */
    current: string | null;
    locale: string;
    width: number;
    onSelect: (language: string) => void;
}

export const DocumentLanguagePicker = ({ variants, current, locale, width, onSelect }: DocumentLanguagePickerProps) => {
    // One entry per language (see engine.languageVariants): the same asset is delivered once per
    // assignment target, and Mantine would otherwise get the same value twice.
    const options = languageVariants(variants);
    const label = current ? languageLabel(current, locale) : '–';

    if (options.length <= 1) {
        return (
            <Text c="dimmed" size="sm">
                {label}
            </Text>
        );
    }

    return (
        <Menu position="bottom-start" radius="md" shadow="md" width={Math.max(width, 170)} zIndex={OVERLAY_DROPDOWN_Z_INDEX}>
            <Menu.Target>
                <UnstyledButton
                    aria-label={`${label} (${options.length})`}
                    className="language-switch-trigger"
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 4,
                        width,
                        padding: '2px 4px 2px 8px',
                        backgroundColor: 'transparent',
                        border: `1px solid ${brand.primary}`,
                        borderRadius: 6,
                        color: brand.primary,
                        fontSize: 14,
                        lineHeight: 1.5
                    }}
                >
                    <span>{label}</span>
                    <span className="material-symbols-outlined" style={{ fontSize: 18 }} aria-hidden>
                        expand_more
                    </span>
                </UnstyledButton>
            </Menu.Target>
            <Menu.Dropdown>
                {options.map((variant) => {
                    const value = variant.language ?? '';
                    const isActive = value === (current ?? '');
                    return (
                        <Menu.Item
                            key={value}
                            fw={isActive ? 700 : 400}
                            leftSection={
                                <span
                                    className="material-symbols-outlined"
                                    style={{ fontSize: 18, opacity: isActive ? 1 : 0 }}
                                    aria-hidden
                                >
                                    check
                                </span>
                            }
                            onClick={() => onSelect(value)}
                        >
                            {variant.language ? languageLabel(variant.language, locale) : '–'}
                        </Menu.Item>
                    );
                })}
            </Menu.Dropdown>
        </Menu>
    );
};
